import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, Shield } from 'lucide-react';
import { api } from '../utils/apiInterceptor';
import styles from './ProfilePage.module.scss';

export const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL
  const getActiveTabFromPath = (): 'profile' | 'security' | 'billing' => {
    if (location.pathname.includes('/security')) return 'security';
    if (location.pathname.includes('/billing')) return 'billing';
    return 'profile';
  };

  const [activeTab, setActiveTab] = useState<
    'profile' | 'security' | 'billing'
  >(getActiveTabFromPath());
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleProfileSave = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.put('/auth/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
      }) as any;

      console.log('[ProfilePage] Profile update response:', response);

      if (response.success || response.data) {
        // Update user context with new data
        const updatedUser = response.data?.user || response.user;
        if (updateUser && updatedUser) {
          updateUser(updatedUser);
        }
        setMessage('Profile updated successfully!');
        setEditMode(false);

        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Debug user object and sync form data
  useEffect(() => {
    console.log('🔍 ProfilePage - Current user object:', user);
    console.log('🔍 ProfilePage - User subscription:', user?.subscription);
    console.log(
      '🔍 ProfilePage - User object keys:',
      user ? Object.keys(user) : 'no user'
    );
    
    // Sync form data with user data when user changes
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!currentPassword || !newPassword) {
      setError('Please fill in all password fields');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.put('/auth/password', {
        currentPassword,
        newPassword,
      }) as any;

      console.log('[ProfilePage] Password update response:', response);

      if (response.success || response.message) {
        setMessage('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // Update tab state when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromPath());
  }, [location.pathname]);

  // Clear messages when form data changes
  useEffect(() => {
    if (error || message) {
      setError('');
      setMessage('');
    }
  }, [formData, currentPassword, newPassword, confirmPassword]);

  const handleTabChange = (tabId: 'profile' | 'security' | 'billing') => {
    const basePath = '/profile';
    const newPath = tabId === 'profile' ? basePath : `${basePath}/${tabId}`;
    navigate(newPath);
    setActiveTab(tabId);
  };

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'security' as const, label: 'Security', icon: Shield },
    { id: 'billing' as const, label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileContainer}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1>Account Settings</h1>
          <p>Manage your profile, security, and billing preferences</p>
        </div>

        {/* Modern Layout */}
        <div className={styles.profileLayout}>
          {/* Sidebar Navigation */}
          <div className={styles.profileSidebar}>
            <nav className={styles.sidebarNav}>
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    className={`${styles.navItem} ${activeTab === tab.id ? styles.navActive : ''}`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className={styles.profileContent}>
            {activeTab === 'profile' && (
              <div className={styles.contentSection}>
                <div className={styles.sectionHeader}>
                  <h2>Profile Information</h2>
                  <button
                    className={`${styles.btn} ${editMode ? styles.btnPrimary : styles.btnSecondary}`}
                    onClick={() =>
                      editMode ? handleProfileSave() : setEditMode(true)
                    }
                    disabled={loading}
                  >
                    {loading
                      ? 'Saving...'
                      : editMode
                        ? 'Save Changes'
                        : 'Edit Profile'}
                  </button>
                </div>

                {message && (
                  <div className={styles.successMessage}>{message}</div>
                )}
                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.profileForm}>
                  <div className={styles.profileFormSection}>
                    <h3>Personal Information</h3>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>First Name</label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              firstName: e.target.value,
                            })
                          }
                          disabled={!editMode}
                          className={styles.formInput}
                          placeholder="Enter your first name"
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Last Name</label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              lastName: e.target.value,
                            })
                          }
                          disabled={!editMode}
                          className={styles.formInput}
                          placeholder="Enter your last name"
                        />
                      </div>

                      <div
                        className={`${styles.formGroup} ${styles.formFullWidth}`}
                      >
                        <label className={styles.formLabel}>
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={e =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          disabled={!editMode}
                          className={styles.formInput}
                          placeholder="Enter your email address"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.infoCards}>
                    <div className={styles.infoCard}>
                      <div className={styles.cardHeader}>
                        <h3>Subscription Plan</h3>
                        <span
                          className={`${styles.statusBadge} ${styles[`status${user?.subscription === 'pro' ? 'Pro' : user?.subscription === 'enterprise' ? 'Enterprise' : 'Free'}`]}`}
                        >
                          {user?.subscription?.toUpperCase() || 'FREE'}
                        </span>
                      </div>
                      <p className={styles.cardDescription}>
                        {user?.subscription === 'free' &&
                          'Access to limited courses and features. Upgrade to unlock more content.'}
                        {user?.subscription === 'pro' &&
                          'Access to advanced courses and premium features. Great choice!'}
                        {user?.subscription === 'enterprise' &&
                          'Unlimited access to all courses and features. Thank you for being our top member!'}
                        {!user?.subscription &&
                          'Access to limited courses and features. Upgrade to unlock more content.'}
                      </p>
                      <div className={styles.cardAction}>
                        {(user?.subscription === 'free' ||
                          !user?.subscription) && (
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={() => navigate('/upgrade-plan')}
                          >
                            Upgrade Plan
                          </button>
                        )}
                        {user?.subscription === 'pro' && (
                          <>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary}`}
                              onClick={() => navigate('/upgrade-plan')}
                            >
                              Upgrade to Enterprise
                            </button>
                            <button
                              className={`${styles.btn} ${styles.btnSecondary}`}
                            >
                              Manage Subscription
                            </button>
                          </>
                        )}
                        {user?.subscription === 'enterprise' && (
                          <button
                            className={`${styles.btn} ${styles.btnSecondary}`}
                          >
                            Manage Subscription
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className={styles.contentSection}>
                <div className={styles.sectionHeader}>
                  <h2>Security Settings</h2>
                </div>

                {message && (
                  <div className={styles.successMessage}>{message}</div>
                )}
                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.securityForm}>
                  <div className={styles.passwordSection}>
                    <h3>Change Password</h3>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        className={styles.formInput}
                        placeholder="Enter your current password"
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className={styles.formInput}
                        placeholder="Enter your new password"
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className={styles.formInput}
                        placeholder="Confirm your new password"
                      />
                    </div>

                    <button
                      className={`${styles.btn} ${styles.btnDanger}`}
                      onClick={handlePasswordChange}
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>

                  <div className={styles.infoCards}>
                    <div className={styles.infoCard}>
                      <div className={styles.cardHeader}>
                        <h3>Email Verification</h3>
                        <span
                          className={`${styles.statusBadge} ${styles[user?.isVerified ? 'statusVerified' : 'statusUnverified']}`}
                        >
                          {user?.isVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                      <p className={styles.cardDescription}>
                        {user?.isVerified
                          ? 'Your email address has been successfully verified.'
                          : 'Please verify your email address to secure your account and unlock all features.'}
                      </p>
                      {!user?.isVerified && (
                        <div className={styles.cardAction}>
                          <button
                            className={`${styles.btn} ${styles.btnSecondary}`}
                          >
                            Send Verification Email
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className={styles.contentSection}>
                <div className={styles.sectionHeader}>
                  <h2>Billing & Subscription</h2>
                </div>

                <div className={styles.billingForm}>
                  {/* Current Plan Section */}
                  <div className={styles.billingSection}>
                    <h3>Current Plan</h3>
                    <div className={styles.currentPlanCard}>
                      <div className={styles.planInfo}>
                        <div className={styles.planName}>
                          {user?.subscription === 'free'
                            ? 'Free Plan'
                            : user?.subscription === 'pro'
                              ? 'Pro Plan'
                              : user?.subscription === 'enterprise'
                                ? 'Enterprise Plan'
                                : 'Free Plan'}
                        </div>
                        <div className={styles.planPrice}>
                          {user?.subscription === 'free'
                            ? '$0/month'
                            : user?.subscription === 'pro'
                              ? '$19/month'
                              : user?.subscription === 'enterprise'
                                ? '$99/month'
                                : '$0/month'}
                        </div>
                      </div>
                      <div className={styles.planStatus}>
                        <span
                          className={`${styles.statusBadge} ${user?.subscription === 'pro' || user?.subscription === 'enterprise' ? styles.statusActive : styles.statusFree}`}
                        >
                          {user?.subscription === 'pro' ||
                          user?.subscription === 'enterprise'
                            ? 'Active'
                            : 'Free'}
                        </span>
                      </div>
                    </div>
                    {(user?.subscription === 'pro' ||
                      user?.subscription === 'enterprise') && (
                      <p className={styles.planDetails}>
                        Next billing date: December 4, 2024
                      </p>
                    )}
                  </div>

                  {/* Payment Method Section */}
                  {(user?.subscription === 'pro' ||
                    user?.subscription === 'enterprise') && (
                    <div className={styles.billingSection}>
                      <h3>Payment Method</h3>
                      <div className={styles.paymentMethod}>
                        <div className={styles.cardInfo}>
                          <span className={styles.cardBrand}>Visa</span>
                          <span className={styles.cardLast4}>•••• 4242</span>
                          <span className={styles.cardExpiry}>12/28</span>
                        </div>
                        <button
                          className={`${styles.btn} ${styles.btnSecondary}`}
                        >
                          Update Card
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Billing History Section */}
                  {(user?.subscription === 'pro' ||
                    user?.subscription === 'enterprise') && (
                    <div className={styles.billingSection}>
                      <h3>Billing History</h3>
                      <div className={styles.billingHistory}>
                        <div className={styles.billingItem}>
                          <div className={styles.billingDate}>Nov 4, 2024</div>
                          <div className={styles.billingDescription}>
                            Pro Plan - Monthly
                          </div>
                          <div className={styles.billingAmount}>$19.00</div>
                          <button
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
                          >
                            Download
                          </button>
                        </div>
                        <div className={styles.billingItem}>
                          <div className={styles.billingDate}>Oct 4, 2024</div>
                          <div className={styles.billingDescription}>
                            Pro Plan - Monthly
                          </div>
                          <div className={styles.billingAmount}>$19.00</div>
                          <button
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
                          >
                            Download
                          </button>
                        </div>
                        <div className={styles.billingItem}>
                          <div className={styles.billingDate}>Sep 4, 2024</div>
                          <div className={styles.billingDescription}>
                            Pro Plan - Monthly
                          </div>
                          <div className={styles.billingAmount}>$19.00</div>
                          <button
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subscription Actions */}
                  <div className={styles.billingSection}>
                    <h3>Subscription Actions</h3>
                    <div className={styles.subscriptionActions}>
                      {!user?.subscription && (
                        <button
                          className={`${styles.btn} ${styles.btnPrimary}`}
                          onClick={() => navigate('/upgrade-plan')}
                        >
                          Choose Plan
                        </button>
                      )}
                      {user?.subscription === 'free' && (
                        <button
                          className={`${styles.btn} ${styles.btnPrimary}`}
                          onClick={() => navigate('/upgrade-plan')}
                        >
                          Upgrade to Pro
                        </button>
                      )}
                      {user?.subscription === 'pro' && (
                        <>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={() => navigate('/upgrade-plan')}
                          >
                            Upgrade to Enterprise
                          </button>
                          <button
                            className={`${styles.btn} ${styles.btnDanger}`}
                          >
                            Cancel Subscription
                          </button>
                        </>
                      )}
                      {user?.subscription === 'enterprise' && (
                        <>
                          <button
                            className={`${styles.btn} ${styles.btnSecondary}`}
                            onClick={() => navigate('/upgrade-plan')}
                          >
                            Downgrade Plan
                          </button>
                          <button
                            className={`${styles.btn} ${styles.btnDanger}`}
                          >
                            Cancel Subscription
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
